'use client';

/**
 * BookingCart Component
 *
 * Shopping cart for booking items with real-time price calculations.
 * Features:
 * - List of selected items (lodges, activities)
 * - Real-time price calculation with breakdown
 * - Edit/remove items functionality
 * - Promo code application
 * - Guest count summary
 * - Responsive mobile-first design
 * - Bilingual support (en/es)
 *
 * @see lib/booking/pricing.ts for price calculations
 * @see types/booking.ts for type definitions
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { es, enUS, ptBR } from 'date-fns/locale';
import { useTranslations } from 'next-intl';
import styles from './BookingCart.module.css';
import { Currency } from '@/types/booking';
import { PriceBreakdown } from '@/types/availability';
import { DualPrice } from '@/components/pricing/DualPrice';

/**
 * Service segment metadata (transport add-ons).
 * Mirrors `BookingSegment` from `@/lib/unified-cart` without forcing the cart
 * component to depend on that module.
 */
export interface CartItemServiceSegment {
  kind: 'outbound' | 'return';
  date: string;   // YYYY-MM-DD
  time: string;   // HH:mm (Chile local)
}

/**
 * Cart item structure for client-side use
 */
export interface CartItem {
  /** Unique identifier for this cart item */
  cartItemId: string;

  /** Item type (lodge, activity, or package) */
  type: 'lodge' | 'activity' | 'package';

  /** Item ID (lodgeId or activityId) */
  id: string;

  /** Display name */
  name: string;

  /** Start date */
  startDate: Date;

  /** End date */
  endDate: Date;

  /** Number of guests */
  guests: number;

  /** Calculated price breakdown (fetched from API) */
  priceBreakdown?: PriceBreakdown;

  /** Loading state for price calculation */
  loadingPrice?: boolean;

  /** Error message if price calculation failed */
  priceError?: string;

  /** Image URL for display */
  imageUrl?: string;

  /**
   * Lodge total in CLP (source of truth from unified cart).
   * When present and currency is non-CLP in priceBreakdown, the cart will still
   * render the line item using the CLP→USD conversion to stay consistent with
   * the cart summary. Prevents "200,000 USD" mislabel bugs.
   */
  priceCLP?: number;

  /** Guesty quote ID (when using Booking Engine API) */
  quoteId?: string;

  /** Guesty rate plan ID (when using Booking Engine API) */
  ratePlanId?: string;

  /** Guesty pricing from quote (source of truth for Guesty-synced units) */
  guestyPricing?: {
    fareAccommodation: number;
    fareCleaning: number;
    totalTaxes: number;
    subTotalPrice: number;
    currency: string;
  };

  /**
   * Service (transport add-on) metadata.
   * When present, the cart row is rendered as a service:
   *  - "Service" / "Servicio" badge instead of "Activity"
   *  - Per-segment date+time list (outbound and optional return)
   *  - No nights/check-in display
   *
   * The line item is still typed as `activity` so callers/backend that group
   * by lodge|activity continue to work (services flow as activity rows in the
   * booking POST). This field is purely a UI hint.
   */
  serviceMeta?: {
    isService: true;
    segments: CartItemServiceSegment[];
  };
}

/**
 * Cart summary totals
 */
export interface CartSummary {
  /** Subtotal before discounts */
  subtotal: number;

  /** Total discounts applied */
  totalDiscounts: number;

  /** Promo discount amount */
  promoDiscount: number;

  /** Final total */
  total: number;

  /** Currency */
  currency: Currency;

  /** Total nights (lodges only) */
  totalNights: number;

  /** Total guests */
  totalGuests: number;

  /** Number of items */
  itemCount: number;
}

/**
 * BookingCart props
 */
interface BookingCartProps {
  /** Cart items */
  items: CartItem[];

  /** Current language */
  locale?: 'en' | 'es' | 'pt';

  /** Currency override */
  currency?: Currency;

  /** Applied promo code */
  promoCode?: string;

  /** Callback when item is removed */
  onRemoveItem?: (cartItemId: string) => void;

  /** Callback when item is edited */
  onEditItem?: (cartItemId: string) => void;

  /** Callback when promo code is applied */
  onApplyPromoCode?: (code: string) => Promise<{ valid: boolean; message?: string }>;

  /** Callback when promo code is removed */
  onRemovePromoCode?: () => void;

  /** Callback to proceed to checkout */
  onCheckout?: () => void;

  /** Whether checkout is enabled */
  checkoutEnabled?: boolean;

  /** Whether cart is in loading state */
  loading?: boolean;

  /** Additional CSS class */
  className?: string;

  /** Whether to show compact view */
  compact?: boolean;

  /** Whether to hide the summary section (when parent already shows it) */
  hideSummary?: boolean;

  /** Visual variant: 'card' (default with background/shadow) or 'flat' (transparent) */
  variant?: 'card' | 'flat';

  /** Coupon type (percentage or fixed amount) */
  couponType?: 'percentage' | 'fixed' | null;

  /** Coupon value (percent number or fixed dollar amount) */
  couponValue?: number;

  /** Total discount amount from coupon in USD */
  couponDiscount?: number;

  /** Currency the coupon's nominal couponValue is denominated in (only meaningful for 'fixed' coupons) */
  couponCurrency?: string | null;
}


/**
 * Format price for display.
 * CLP has no fractional unit; USD/BRL/EUR show 2 decimals (no rounding).
 */
function formatPrice(amount: number, currency: Currency): string {
  const isCLP = currency === 'CLP';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: isCLP ? 0 : 2,
    maximumFractionDigits: isCLP ? 0 : 2,
  }).format(amount);
}

/**
 * Calculate nights between two dates
 */
function calculateNights(start: Date, end: Date): number {
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Generate unique cart item ID
 */
export function generateCartItemId(): string {
  return `cart-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * BookingCart component
 */
export default function BookingCart({
  items,
  locale = 'en',
  currency = 'USD',
  promoCode,
  onRemoveItem,
  onEditItem,
  onApplyPromoCode,
  onRemovePromoCode,
  onCheckout,
  checkoutEnabled = true,
  loading = false,
  className,
  compact = false,
  hideSummary = false,
  variant = 'card',
  couponType,
  couponValue,
  couponDiscount,
  couponCurrency,
}: BookingCartProps) {
  // State
  const [promoInput, setPromoInput] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Translations
  const t = useTranslations('bookingCart');

  // Date locale
  const dateLocale = locale === 'es' ? es : locale === 'pt' ? ptBR : enUS;

  // Format a service segment label: "Outbound: May 16, 09:30 CLT"
  const formatServiceSegment = useCallback(
    (seg: CartItemServiceSegment): string => {
      // Parse YYYY-MM-DD as local noon to avoid TZ drift
      const date = new Date(seg.date + 'T12:00:00');
      const datePart = format(date, locale === 'en' ? 'MMM d' : 'd MMM', { locale: dateLocale });
      const kindKey: 'outbound' | 'return' = seg.kind === 'return' ? 'return' : 'outbound';
      const kindLabel = t(`segment.${kindKey}`);
      return `${kindLabel}: ${datePart}, ${seg.time}`;
    },
    [locale, dateLocale, t]
  );

  // Toggle item details expansion
  const toggleItemDetails = useCallback((cartItemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(cartItemId)) {
        next.delete(cartItemId);
      } else {
        next.add(cartItemId);
      }
      return next;
    });
  }, []);

  // Calculate cart summary
  const summary = useMemo((): CartSummary => {
    let subtotal = 0;
    let totalDiscounts = 0;
    let promoDiscountTotal = 0;
    let totalNights = 0;
    let totalGuests = 0;

    for (const item of items) {
      if (item.priceBreakdown) {
        subtotal += item.priceBreakdown.subtotal;
        totalDiscounts += item.priceBreakdown.totalDiscounts;
        promoDiscountTotal += item.priceBreakdown.promoDiscount;
      }

      if (item.type === 'lodge' || item.type === 'package') {
        totalNights += calculateNights(item.startDate, item.endDate);
      }

      totalGuests = Math.max(totalGuests, item.guests);
    }

    return {
      subtotal,
      totalDiscounts,
      promoDiscount: promoDiscountTotal,
      total: subtotal - totalDiscounts,
      currency,
      totalNights,
      totalGuests,
      itemCount: items.length,
    };
  }, [items, currency]);

  // Handle promo code submission
  const handleApplyPromo = useCallback(async () => {
    if (!promoInput.trim() || !onApplyPromoCode) return;

    setPromoLoading(true);
    setPromoError(null);
    setPromoSuccess(false);

    try {
      const result = await onApplyPromoCode(promoInput.trim().toUpperCase());

      if (result.valid) {
        setPromoSuccess(true);
        setPromoInput('');
        // Clear success message after 3 seconds
        setTimeout(() => setPromoSuccess(false), 3000);
      } else {
        setPromoError(result.message || t('invalidCode'));
      }
    } catch {
      setPromoError(t('invalidCode'));
    } finally {
      setPromoLoading(false);
    }
  }, [promoInput, onApplyPromoCode, t]);

  // Handle promo code removal
  const handleRemovePromo = useCallback(() => {
    onRemovePromoCode?.();
    setPromoError(null);
    setPromoSuccess(false);
  }, [onRemovePromoCode]);

  // Clear promo messages when input changes
  useEffect(() => {
    if (promoInput) {
      setPromoError(null);
      setPromoSuccess(false);
    }
  }, [promoInput]);

  // Check if any item is loading price
  const isCalculating = items.some((item) => item.loadingPrice);

  // Empty cart state
  if (items.length === 0) {
    return (
      <div className={`${styles.container} ${variant === 'flat' ? styles.flat : ''} ${styles.empty} ${className || ''}`}>
        <div className={styles.emptyIcon}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
        </div>
        <h3 className={styles.emptyTitle}>{t('emptyCart')}</h3>
        <p className={styles.emptyText}>{t('addItems')}</p>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${variant === 'flat' ? styles.flat : ''} ${compact ? styles.compact : ''} ${className || ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>{t('cartTitle')}</h3>
        <span className={styles.itemCount}>
          {t('itemCount', { count: summary.itemCount })}
        </span>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className={styles.loadingOverlay}>
          <span className={styles.spinner} />
        </div>
      )}

      {/* Cart items */}
      <div className={styles.items}>
        {items.map((item) => {
          const nights = calculateNights(item.startDate, item.endDate);
          const isExpanded = expandedItems.has(item.cartItemId);
          const hasBreakdown = item.priceBreakdown && !item.loadingPrice;

          const isLodgeItem = item.type === 'lodge' || item.type === 'package';

          return (
            <div key={item.cartItemId} className={styles.item}>
              {/* Item header */}
              <div className={styles.itemHeader}>
                <div className={styles.itemImage}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} />
                  ) : (
                    <div className={styles.itemImagePlaceholder} aria-hidden />
                  )}
                </div>
                <div className={styles.itemInfo}>
                  <span className={styles.itemType}>
                    {item.serviceMeta?.isService
                      ? t('service')
                      : item.type === 'lodge'
                      ? t('lodge')
                      : item.type === 'activity'
                      ? t('activity')
                      : t('package')}
                  </span>
                  <h4 className={styles.itemName}>{item.name}</h4>
                  <div className={styles.itemDates}>
                    {item.serviceMeta?.isService ? (
                      <>
                        {item.serviceMeta.segments.map((seg, sIdx) => (
                          <span key={`${seg.kind}-${sIdx}`}>{formatServiceSegment(seg)}</span>
                        ))}
                      </>
                    ) : item.type === 'lodge' || item.type === 'package' ? (
                      <>
                        <span>
                          {t('checkIn')}: {format(item.startDate, 'MMM d', { locale: dateLocale })}
                        </span>
                        <span className={styles.dateSeparator}>→</span>
                        <span>
                          {t('checkOut')}: {format(item.endDate, 'MMM d', { locale: dateLocale })}
                        </span>
                      </>
                    ) : (
                      <span>
                        {t('date')}: {format(item.startDate, 'PPP', { locale: dateLocale })}
                      </span>
                    )}
                  </div>
                  <div className={styles.itemMeta}>
                    {(item.type === 'lodge' || item.type === 'package') && (
                      <span>{t('nights', { count: nights })}</span>
                    )}
                    <span>{t('guests', { count: item.guests })}</span>
                  </div>
                </div>
                <div className={styles.itemPrice}>
                  {item.loadingPrice ? (
                    <span className={styles.calculating}>{t('calculating')}</span>
                  ) : item.priceError ? (
                    <span className={styles.priceError}>{item.priceError}</span>
                  ) : item.priceBreakdown ? (() => {
                    // Lodge totals are stored in CLP in the unified cart. Prefer
                    // priceCLP (truth) for the headline price so it stays in sync
                    // with the cart summary, regardless of what currency Guesty/
                    // local pricing tagged the breakdown with.
                    const isLodge = item.type === 'lodge' || item.type === 'package';
                    const useCLPDisplay =
                      isLodge && (item.priceCLP !== undefined || item.priceBreakdown.currency === 'CLP');
                    const totalCLP = item.priceCLP ?? item.priceBreakdown.total;
                    const subtotalCLP = item.priceCLP !== undefined && item.priceBreakdown.subtotal && item.priceBreakdown.total
                      ? Math.round((item.priceBreakdown.subtotal / item.priceBreakdown.total) * item.priceCLP)
                      : item.priceBreakdown.subtotal;
                    const perNightCLP = item.priceCLP !== undefined && item.priceBreakdown.pricePerNight && item.priceBreakdown.total
                      ? Math.round((item.priceBreakdown.pricePerNight / item.priceBreakdown.total) * item.priceCLP)
                      : item.priceBreakdown.pricePerNight;

                    return (
                      <>
                        <span className={styles.priceAmount}>
                          {useCLPDisplay ? (
                            <DualPrice
                              amountCLP={totalCLP}
                              size="md"
                              showCurrencyCode={true}
                              showCLP={false}
                              locale={locale}
                            />
                          ) : (
                            formatPrice(item.priceBreakdown.total, item.priceBreakdown.currency)
                          )}
                        </span>
                        {item.priceBreakdown.totalDiscounts > 0 && (
                          <span className={styles.originalPrice}>
                            {useCLPDisplay ? (
                              <DualPrice
                                amountCLP={subtotalCLP}
                                size="sm"
                                showCurrencyCode={false}
                                showCLP={false}
                                locale={locale}
                              />
                            ) : (
                              formatPrice(item.priceBreakdown.subtotal, item.priceBreakdown.currency)
                            )}
                          </span>
                        )}
                        {isLodge && perNightCLP && (
                          <span className={styles.priceUnit}>
                            {useCLPDisplay ? (
                              <DualPrice
                                amountCLP={perNightCLP}
                                size="sm"
                                showCurrencyCode={false}
                                showCLP={false}
                                locale={locale}
                              />
                            ) : (
                              formatPrice(
                                item.priceBreakdown.pricePerNight!,
                                item.priceBreakdown.currency
                              )
                            )}
                            {nights > 1 ? t('perNightAvg') : t('perNight')}
                          </span>
                        )}
                        {item.type === 'activity' && item.priceBreakdown.pricePerPerson && (
                          <span className={styles.priceUnit}>
                            {formatPrice(item.priceBreakdown.pricePerPerson, item.priceBreakdown.currency)}
                            {t('perPerson')}
                          </span>
                        )}
                      </>
                    );
                  })() : null}
                </div>
              </div>

              {/* Item actions */}
              <div className={styles.itemActions}>
                {hasBreakdown && (
                  <button
                    type="button"
                    className={styles.detailsButton}
                    onClick={() => toggleItemDetails(item.cartItemId)}
                  >
                    {isExpanded ? t('hideDetails') : t('viewDetails')}
                  </button>
                )}
                {onEditItem && (
                  <button
                    type="button"
                    className={styles.editButton}
                    onClick={() => onEditItem(item.cartItemId)}
                  >
                    {t('edit')}
                  </button>
                )}
                {onRemoveItem && (
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => onRemoveItem(item.cartItemId)}
                  >
                    {t('remove')}
                  </button>
                )}
              </div>

              {/* Price breakdown (expanded) */}
              {isExpanded && hasBreakdown && item.priceBreakdown && (
                <div className={styles.breakdown}>
                  {/* 1. Price per night — "average" label when the stay has more than one night */}
                  {isLodgeItem && item.priceBreakdown.pricePerNight && (
                    <div className={styles.breakdownRow}>
                      <span>{nights > 1 ? t('avgPricePerNight') : t('pricePerNight')}</span>
                      <span>
                        {item.priceBreakdown.currency === 'CLP' ? (
                          <DualPrice
                            amountCLP={item.priceBreakdown.pricePerNight}
                            size="sm"
                            showCurrencyCode={false}
                            showCLP={false}
                            locale={locale}
                          />
                        ) : (
                          formatPrice(item.priceBreakdown.pricePerNight, item.priceBreakdown.currency)
                        )}
                      </span>
                    </div>
                  )}
                  {/* 2. Number of nights (lodges/packages) */}
                  {(item.type === 'lodge' || item.type === 'package') && item.priceBreakdown.nights && (
                    <div className={styles.breakdownRow}>
                      <span>{t('numberOfNights')}</span>
                      <span>{item.priceBreakdown.nights}</span>
                    </div>
                  )}
                  {/* 3. Base price — only for non-lodge items (activities/transport), where it's the item price, not a per-night sum */}
                  {item.type !== 'lodge' && item.type !== 'package' && (
                    <div className={styles.breakdownRow}>
                      <span>{t('basePrice')}</span>
                      <span>
                        {item.priceBreakdown.currency === 'CLP' ? (
                          <DualPrice
                            amountCLP={item.priceBreakdown.basePrice}
                            size="sm"
                            showCurrencyCode={false}
                            showCLP={false}
                            locale={locale}
                          />
                        ) : (
                          formatPrice(item.priceBreakdown.basePrice, item.priceBreakdown.currency)
                        )}
                      </span>
                    </div>
                  )}
                  {item.priceBreakdown.extraGuestFees > 0 && (
                    <div className={styles.breakdownRow}>
                      <span>{t('extraGuests')}</span>
                      <span>
                        +
                        {item.priceBreakdown.currency === 'CLP' ? (
                          <DualPrice
                            amountCLP={item.priceBreakdown.extraGuestFees}
                            size="sm"
                            showCurrencyCode={false}
                            showCLP={false}
                            locale={locale}
                          />
                        ) : (
                          formatPrice(
                            item.priceBreakdown.extraGuestFees,
                            item.priceBreakdown.currency
                          )
                        )}
                      </span>
                    </div>
                  )}
                  {item.priceBreakdown.cleaningFee > 0 && (
                    <div className={styles.breakdownRow}>
                      <span>{t('cleaningFee')}</span>
                      <span>
                        +
                        {item.priceBreakdown.currency === 'CLP' ? (
                          <DualPrice
                            amountCLP={item.priceBreakdown.cleaningFee}
                            size="sm"
                            showCurrencyCode={false}
                            showCLP={false}
                            locale={locale}
                          />
                        ) : (
                          formatPrice(item.priceBreakdown.cleaningFee, item.priceBreakdown.currency)
                        )}
                      </span>
                    </div>
                  )}
                  {item.priceBreakdown.groupDiscount > 0 && (
                    <div className={styles.breakdownRow}>
                      <span>{t('groupDiscount')}</span>
                      <span className={styles.discountAmount}>
                        -
                        {item.priceBreakdown.currency === 'CLP' ? (
                          <DualPrice
                            amountCLP={item.priceBreakdown.groupDiscount}
                            size="sm"
                            showCurrencyCode={false}
                            showCLP={false}
                            locale={locale}
                          />
                        ) : (
                          formatPrice(
                            item.priceBreakdown.groupDiscount,
                            item.priceBreakdown.currency
                          )
                        )}
                      </span>
                    </div>
                  )}
                  {item.priceBreakdown.promoDiscount > 0 && (
                    <div className={styles.breakdownRow}>
                      <span>{t('promoDiscount')}</span>
                      <span className={styles.discountAmount}>
                        -
                        {item.priceBreakdown.currency === 'CLP' ? (
                          <DualPrice
                            amountCLP={item.priceBreakdown.promoDiscount}
                            size="sm"
                            showCurrencyCode={false}
                            showCLP={false}
                            locale={locale}
                          />
                        ) : (
                          formatPrice(
                            item.priceBreakdown.promoDiscount,
                            item.priceBreakdown.currency
                          )
                        )}
                      </span>
                    </div>
                  )}
                  {/* Item total */}
                  <div className={`${styles.breakdownRow} ${styles.breakdownTotal}`}>
                    <span><strong>{t('itemTotal')}</strong></span>
                    <span>
                      <strong>
                        {item.priceBreakdown.currency === 'CLP' ? (
                          <DualPrice
                            amountCLP={item.priceBreakdown.total}
                            size="sm"
                            showCurrencyCode={true}
                            showCLP={false}
                            locale={locale}
                          />
                        ) : (
                          formatPrice(item.priceBreakdown.total, item.priceBreakdown.currency)
                        )}
                      </strong>
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Promo code section */}
      {onApplyPromoCode && (
        <div className={styles.promoSection}>
          {promoCode ? (
            <div className={styles.appliedPromo}>
              <div className={styles.appliedPromoHeader}>
                <span className={styles.promoLabel}>{t('promoCode')}:</span>
                <span className={styles.promoValue}>{promoCode}</span>
                {couponType === 'percentage' && couponValue ? (
                  <span className={styles.discountBadge}>-{couponValue}%</span>
                ) : couponType === 'fixed' && couponDiscount ? (
                  <span className={styles.discountBadge}>
                    -{formatPrice(couponDiscount, 'USD')}
                    {couponCurrency === 'CLP' && couponValue ? (
                      <> ({new Intl.NumberFormat('es-CL').format(couponValue)} CLP)</>
                    ) : null}
                  </span>
                ) : null}
                {couponDiscount != null && couponDiscount > 0 && (
                  <span className={styles.discountSavings}>
                    {t('savings')}: -${couponDiscount.toFixed(2)}
                  </span>
                )}
                <button type="button" className={styles.removePromoButton} onClick={handleRemovePromo}>
                  {t('removeCode')}
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.promoInput}>
              <input
                type="text"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder={t('promoCode')}
                className={styles.promoField}
                disabled={promoLoading}
              />
              <button
                type="button"
                className={styles.applyPromoButton}
                onClick={handleApplyPromo}
                disabled={!promoInput.trim() || promoLoading}
              >
                {promoLoading ? '...' : t('applyCode')}
              </button>
            </div>
          )}
          {promoError && <p className={styles.promoError}>{promoError}</p>}
          {promoSuccess && <p className={styles.promoSuccess}>{t('codeApplied')}</p>}
        </div>
      )}

      {/* Summary section - hidden when parent already shows summary */}
      {!hideSummary && (
        <div className={styles.summary}>
          <h4 className={styles.summaryTitle}>{t('summary')}</h4>

          {/* Summary rows */}
          <div className={styles.summaryRows}>
            <div className={styles.summaryRow}>
              <span>{t('subtotal')}</span>
              <span>
                {summary.currency === 'CLP' ? (
                  <DualPrice
                    amountCLP={summary.subtotal}
                    size="sm"
                    showCurrencyCode={false}
                    showCLP={false}
                    locale={locale}
                  />
                ) : (
                  formatPrice(summary.subtotal, summary.currency)
                )}
              </span>
            </div>
            {summary.totalDiscounts > 0 && (
              <div className={`${styles.summaryRow} ${styles.discountRow}`}>
                <span>{t('discount')}</span>
                <span className={styles.discountAmount}>
                  -
                  {summary.currency === 'CLP' ? (
                    <DualPrice
                      amountCLP={summary.totalDiscounts}
                      size="sm"
                      showCurrencyCode={false}
                      showCLP={false}
                      locale={locale}
                    />
                  ) : (
                    formatPrice(summary.totalDiscounts, summary.currency)
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Total */}
          <div className={styles.totalRow}>
            <span>{t('total')}</span>
            <span className={styles.totalAmount}>
              {isCalculating ? (
                t('calculating')
              ) : summary.currency === 'CLP' ? (
                <DualPrice
                  amountCLP={summary.total}
                  size="md"
                  showCurrencyCode={true}
                  showCLP={false}
                  locale={locale}
                />
              ) : (
                formatPrice(summary.total, summary.currency)
              )}
            </span>
          </div>

          {/* Meta info */}
          {!compact && (
            <div className={styles.summaryMeta}>
              {summary.totalNights > 0 && (
                <span>{t('nights', { count: summary.totalNights })}</span>
              )}
              <span>{t('guests', { count: summary.totalGuests })}</span>
            </div>
          )}
        </div>
      )}

      {/* Checkout button */}
      {onCheckout && (
        <button
          type="button"
          className={styles.checkoutButton}
          onClick={onCheckout}
          disabled={!checkoutEnabled || isCalculating || items.length === 0}
        >
          {t('proceedToCheckout')}
        </button>
      )}
    </div>
  );
}
